import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { fmt } from '../../format';
import { useSession } from '../../session';
import type { CredentialView, StockListingView, StockSyncView } from '../../types';
import { ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';

/**
 * Stock sent to one marketplace account: off until the seller switches it on, every listing
 * with the quantity it gets and why, a safety margin for the account and per listing.
 */
export function StockSyncDialog({ credential, onClose, onChange }: { credential: CredentialView; onClose: () => void; onChange: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const writer = me.role === 'owner' || me.role === 'manager';
  const url = `/api/integrations/credentials/${credential.id}/stock`;
  // read again every few seconds while open: pushes run in the background
  const view = useLoad(() => api<StockSyncView>('GET', url), [url], 5_000);
  const [safety, setSafety] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const sort = useSort<StockListingView>('stock-sync', {
    product: { label: 'Ürün', value: (l) => l.sku },
    channelSku: { label: 'Pazaryerindeki kodu', value: (l) => l.channelSku },
    available: { label: 'Depoda satılabilir', value: (l) => l.available, desc: true },
    demand: { label: 'Bekleyen sipariş', value: (l) => l.openDemand, desc: true },
    safety: { label: 'Emniyet payı', value: (l) => l.safety, desc: true },
    quantity: { label: 'Gönderilecek', value: (l) => l.quantity, desc: true },
    // the quantity the cell shows; a listing that could not be sent shows none
    pushed: { label: 'Son gönderilen', value: (l) => (l.problem || l.pushError ? null : l.lastPushedQty), desc: true },
  });

  const configure = async (body: { enabled?: boolean; safetyQty?: number }, done: string) => {
    setBusy(true);
    try {
      await api<StockSyncView>('PUT', url, body);
      toast(done, 'ok');
      setSafety(null);
      setConfirming(false);
      await view.reload();
      onChange();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  const listing = async (l: StockListingView, body: { included?: boolean; safetyQty?: number | null }) => {
    try {
      await api('PUT', `${url}/listings/${l.listingId}`, body);
      await view.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const pushNow = async () => {
    setBusy(true);
    try {
      await api('POST', `${url}/push`);
      toast('Stoklar birkaç saniye içinde gönderilecek', 'ok');
      setTimeout(() => void view.reload(), 8_000);
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  const v = view.data;
  const safetyValue = safety ?? String(v?.safetyQty ?? 0);
  const safetyNumber = /^\d+$/.test(safetyValue.trim()) ? Number(safetyValue) : NaN;

  return (
    <Modal title={`${credential.label} · stok gönderimi`} onClose={onClose} wide>
      {view.error && <ErrorBanner message={view.error} onRetry={() => void view.reload()} />}
      {!v ? (
        <Spinner />
      ) : (
        <div className="stack">
          <div className="row">
            <span className={`chip ${v.enabled ? 'ok' : ''}`}>{v.enabled ? 'Açık' : 'Kapalı'}</span>
            <span className="muted">
              {v.syncedAt ? `Son gönderim ${fmt.dateTime(v.syncedAt)}` : 'Henüz gönderilmedi'}
              {v.enabled && v.pending > 0 ? ` · ${v.pending} ürün gönderilmeyi bekliyor` : ''}
            </span>
            <span className="spacer" style={{ flex: 1 }} />
            {v.enabled && (
              <button className="btn sm" disabled={!writer || busy} onClick={() => void pushNow()}>
                <Icon name="send" size={16} /> Şimdi gönder
              </button>
            )}
          </div>
          {v.error && <div className="banner bad">Son gönderim başarısız: {v.error}</div>}
          {!v.supported && <div className="banner warn">Bu pazaryerine stok gönderimi henüz desteklenmiyor.</div>}

          <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>
            Pazaryerinde görünen stok = depodaki satılabilir stok − bekleyen siparişler − emniyet payı. Hasarlı, karantinadaki, iadede bekleyen ve
            son kullanma tarihi geçmiş (ya da raf ömrü yetmeyen) ürünler sayılmaz. Stok değiştiğinde bir dakika içinde, ayrıca her gece tümü gönderilir.
          </p>

          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div className="field" style={{ maxWidth: 220 }}>
              <label htmlFor="stock-safety">Emniyet payı (her üründen)</label>
              <div className="unit-input">
                <input id="stock-safety" className="input" inputMode="numeric" value={safetyValue} disabled={!writer} onChange={(e) => setSafety(e.target.value.replace(/\D/g, ''))} />
                <span>adet</span>
              </div>
            </div>
            {safety !== null && (
              <button className="btn" disabled={!writer || busy || !Number.isInteger(safetyNumber)} onClick={() => void configure({ safetyQty: safetyNumber }, 'Emniyet payı kaydedildi')}>
                Kaydet
              </button>
            )}
            <span className="spacer" style={{ flex: 1 }} />
            {v.enabled ? (
              <button className="btn" disabled={!writer || busy} onClick={() => void configure({ enabled: false }, 'Stok gönderimi kapatıldı')}>
                Stok gönderimini kapat
              </button>
            ) : (
              <button className="btn primary" disabled={!writer || busy || !v.supported} onClick={() => setConfirming(true)}>
                Stok gönderimini aç
              </button>
            )}
          </div>

          {confirming && (
            <div className="banner warn">
              <div>
                Açtığınızda bu hesaptaki eşleşmiş {v.listings.filter((l) => l.included).length} ürünün {credential.label} stoğu depodaki satılabilir stokla
                değiştirilir.
                {v.zero > 0 && (
                  <>
                    {' '}
                    <b>{v.zero} ürün 0 gönderilecek ve satışa kapanacak</b> (depoda satılabilir stoğu yok). Açılış stoğunu girmeden açmayın.
                  </>
                )}
                <div className="row" style={{ marginTop: 8 }}>
                  <button className="btn sm primary" disabled={busy} onClick={() => void configure({ enabled: true, safetyQty: Number.isInteger(safetyNumber) ? safetyNumber : undefined }, 'Stok gönderimi açıldı')}>
                    Evet, aç
                  </button>
                  <button className="btn sm" onClick={() => setConfirming(false)}>
                    Vazgeç
                  </button>
                </div>
              </div>
            </div>
          )}

          {v.listings.length === 0 ? (
            <div className="banner info">Bu hesapta eşleşmiş ürün yok. Ürünler ve stok → Pazaryeri eşleştirme ekranından pazaryeri ürünlerini kendi ürünlerinizle eşleştirin.</div>
          ) : (
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th>Gönder</th>
                    {sort.th('product')}
                    {sort.th('channelSku')}
                    {sort.th('available', 'num')}
                    {sort.th('demand', 'num')}
                    {sort.th('safety', 'num')}
                    {sort.th('quantity', 'num')}
                    {sort.th('pushed')}
                  </tr>
                </thead>
                <tbody>
                  {sort.apply(v.listings).map((l) => (
                    <tr key={l.listingId} className={l.included ? '' : 'muted-row'}>
                      <td>
                        <input type="checkbox" checked={l.included} disabled={!writer} onChange={(e) => void listing(l, { included: e.target.checked })} aria-label={`${l.channelSku} stoğu gönderilsin`} />
                      </td>
                      <td>
                        <b className="mono" style={{ whiteSpace: 'nowrap' }}>
                          {l.sku}
                        </b>
                        <div className="muted" style={{ fontSize: 12.5 }}>
                          {l.name}
                        </div>
                      </td>
                      <td className="mono">
                        {l.channelSku}
                        {l.barcode && l.barcode !== l.channelSku && <div className="muted">{l.barcode}</div>}
                        {l.unitsPerListing > 1 && <span className="chip info">{l.unitsPerListing}'li paket</span>}
                      </td>
                      <td className="num">{fmt.n(l.available)}</td>
                      <td className="num">{l.openDemand > 0 ? fmt.n(l.openDemand) : '—'}</td>
                      <td className="num">
                        <SafetyCell listing={l} disabled={!writer} onSave={(qty) => void listing(l, { safetyQty: qty })} />
                      </td>
                      <td className="num">
                        <b>{fmt.n(l.quantity)}</b>
                      </td>
                      <td>
                        {l.problem ? (
                          <span className="chip bad" title={l.problem}>
                            Gönderilemiyor
                          </span>
                        ) : l.pushError ? (
                          <span className="chip bad" title={l.pushError}>
                            Reddedildi
                          </span>
                        ) : l.lastPushedQty !== null ? (
                          <span className={`chip ${l.lastPushedQty === l.quantity ? 'ok' : 'warn'}`} title={l.lastPushedAt ? fmt.dateTime(l.lastPushedAt) : undefined}>
                            {fmt.n(l.lastPushedQty)}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                        {(l.problem ?? l.pushError) && (
                          <div className="muted" style={{ fontSize: 12, maxWidth: 260 }}>
                            {l.problem ?? l.pushError}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/** The listing's own margin: empty means the account's. */
function SafetyCell({ listing, disabled, onSave }: { listing: StockListingView; disabled: boolean; onSave: (qty: number | null) => void }) {
  const [value, setValue] = useState(listing.safetyOverride === null ? '' : String(listing.safetyOverride));
  const save = () => {
    const next = value.trim() === '' ? null : Number(value);
    if (next !== listing.safetyOverride && (next === null || Number.isInteger(next))) onSave(next);
  };
  return (
    <input
      className="input"
      style={{ width: 72, minHeight: 34, textAlign: 'right' }}
      inputMode="numeric"
      value={value}
      placeholder={String(listing.safety)}
      disabled={disabled}
      onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
      onBlur={save}
      onKeyDown={(e) => e.key === 'Enter' && save()}
      aria-label={`${listing.channelSku} emniyet payı`}
      title="Boş bırakılırsa hesabın emniyet payı geçerli"
    />
  );
}
